import { useEffect, useId, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  MotifIcon,
  MotifButton,
  MotifFormField,
  MotifErrorMessage,
  MotifLabel,
} from "@ey-xd/motif-react";
import {
  MotifNavigationIcClose24px,
  IconoirArrowUpTag,
  IconoirPlus,
} from "@ey-xd/motif-icon";
import { AppDispatch, RootState } from "../../store";

import SearchInput from "../SearchInput/SearchInput";
import "./EmailSearch.scss";
import {
  getUserSearchData,
  resetUserSearchData,
} from "../../store/slices/UsersSlice";

interface SharedUsers {
  Name: string;
  Email: string;
}
type EmailSearchProps = {
  newUserList: SharedUsers[];
  setNewUserList: React.Dispatch<React.SetStateAction<SharedUsers[]>>;
  setSharedEmailValidationError: React.Dispatch<React.SetStateAction<boolean>>;
  sharedUserList: SharedUsers[];
};

const EmailSearch = ({
  newUserList,
  setNewUserList,
  setSharedEmailValidationError,
  sharedUserList,
}: EmailSearchProps) => {
  const [userSearch, setUserSearch] = useState<string[]>([]);
  const [userCurrentSearch, setUserCurrentSearch] = useState<string>("");
  const [searchCharCount, setSearchCharCount] = useState(0);
  const [sharedUsers, setSharedUsers] = useState<SharedUsers[]>(newUserList);
  const [showUsersSearch, setShowUsersSearch] = useState(
    newUserList && newUserList.length > 0 ? false : true,
  );
  const [isAlreadyShared, setIsAlreadyShared] = useState(false);

  const [expandProxyOwnerSearch, setExpandProxyOwnerSearch] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const searchAbortRef = useRef<{ abort: () => void } | null>(null);
  useEffect(() => {
    setSharedUsers(newUserList);
    setNewUserList(newUserList);
    setShowUsersSearch(newUserList && newUserList.length > 0 ? false : true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newUserList]);

  const userList = useSelector((state: RootState) => state.UsersSlice.data);
  const userListLoading = useSelector(
    (state: RootState) => state.UsersSlice.isLoading,
  );

  const emailId: string = useSelector(
    (state: RootState) => state.UserProfileSlice.emailId,
  );
  const clearSearch = () => {
    setUserCurrentSearch("");
    setUserSearch([]);
    dispatch(resetUserSearchData());
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputValue = event.target.value;
    if (inputValue.trim().length == 0) {
      clearSearch();
    } else {
      setSearchCharCount(inputValue?.length);
      setUserCurrentSearch(inputValue);
      if (inputValue.trim().length >= 3) {
        if (searchAbortRef.current) {
          searchAbortRef.current.abort();
        }
        searchAbortRef.current = dispatch(
          getUserSearchData({ searchText: inputValue.trim() }),
        ) as unknown as { abort: () => void };
      }
    }
  };

  const handleUserClick = (userData: string) => {
    if (!userData) return;
    const newProxyOwner = {
      Name: userData.split("(")[0].trim(),
      Email: userData.split("(")[1].split(")")[0].trim(),
    };
    setSharedUsers([...sharedUsers, newProxyOwner]);
    setNewUserList([...sharedUsers, newProxyOwner]);
    setUserSearch([...userSearch, userCurrentSearch]);
    setUserCurrentSearch("");
    dispatch(resetUserSearchData());
    setShowUsersSearch(false);
  };

  const handleDeleteClick = (event: any, email: string) => {
    event.stopPropagation();
    if (sharedUsers.length === 1) {
      setShowUsersSearch(true);
      setNewUserList([]);
    }
    const filetrEmailsList = sharedUsers.filter((user) => {
      return user.Email !== email;
    });

    setNewUserList(filetrEmailsList);
    setSharedUsers(filetrEmailsList);
  };

  const handleSelectedUserGridClick = () => {
    setShowUsersSearch(!showUsersSearch);
    setUserCurrentSearch("");
  };

  const filteredItems =
    userList?.filter((userData: any) => {
      if (
        sharedUserList.find((sharedUser) => sharedUser.Email === userData.email)
      ) {
        return false;
      }
      if (userData.email === emailId) {
        return false;
      }
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
      return emailRegex.test(userData.email);
    }) || [];

  const displayItems = filteredItems.map(
    (userData: any) => `${userData.displayName} (${userData.email})`,
  );

  const checkIfAlreadyShared = () => {
    if (userList && userCurrentSearch.trim().length > 3) {
      const matchedUser = userList.find((userData: any) => {
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
        if (!emailRegex.test(userData.email)) return false;
        return (
          userData.displayName
            .toLowerCase()
            .includes(userCurrentSearch.toLowerCase()) ||
          userData.email.toLowerCase().includes(userCurrentSearch.toLowerCase())
        );
      });

      if (matchedUser) {
        return sharedUserList.some(
          (sharedUser) => sharedUser.Email === matchedUser.email,
        );
      }
    }
    return false;
  };

  const checkIfSearchingOwnEmail = () => {
    if (userList && userCurrentSearch.trim().length > 3 && emailId) {
      return userList.some((userData: any) => {
        if (userData.email !== emailId) return false;
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
        if (!emailRegex.test(userData.email)) return false;
        return (
          userData.displayName
            .toLowerCase()
            .includes(userCurrentSearch.toLowerCase()) ||
          userData.email.toLowerCase().includes(userCurrentSearch.toLowerCase())
        );
      });
    }
    return false;
  };

  useEffect(() => {
    const alreadyShared = checkIfAlreadyShared();
    setIsAlreadyShared(alreadyShared);
    if (
      userCurrentSearch.trim().length > 3 &&
      userList &&
      (!displayItems.length || alreadyShared)
    ) {
      setSharedEmailValidationError(true);
    } else {
      setSharedEmailValidationError(false);
    }
  }, [
    userCurrentSearch,
    userList,
    displayItems.length,
    setSharedEmailValidationError,
    sharedUserList,
  ]);

  return (
    <div className="email-search-section">
      <div className="email-selected-users-section">
        {sharedUsers && sharedUsers.length > 0 && (
          <>
            <MotifLabel className="email-label">
              Enter the recipient name or email
            </MotifLabel>
            <div
              className={`selected-users`}
              onClick={handleSelectedUserGridClick}
            >
              {sharedUsers.map((user, index) => {
                if (index > 1) {
                  return (
                    <div
                      key={index}
                      className={`selected-user ${
                        expandProxyOwnerSearch ? "" : "hide"
                      }`}
                    >
                      {user.Name}
                      <span
                        className="remove-user"
                        onClick={(e) => handleDeleteClick(e, user.Email)}
                      >
                        <MotifIcon iconFunction={MotifNavigationIcClose24px} />
                      </span>
                    </div>
                  );
                }
                return (
                  <div key={index} className="selected-user">
                    {user.Name}
                    <span
                      className="remove-user"
                      onClick={(e) => handleDeleteClick(e, user.Email)}
                    >
                      <MotifIcon
                        iconFunction={MotifNavigationIcClose24px}
                        fill="#A4A3B1"
                        size="10"
                      />
                    </span>
                  </div>
                );
              })}
              {sharedUsers.length > 2 && (
                <div
                  className="selected-user collapsed-grid"
                  onClick={(event) => {
                    event.stopPropagation();
                    setExpandProxyOwnerSearch(!expandProxyOwnerSearch);
                  }}
                >
                  {expandProxyOwnerSearch ? (
                    <span>
                      <MotifIcon
                        size="24px"
                        className="collapse-btn"
                        iconFunction={IconoirArrowUpTag}
                      />
                    </span>
                  ) : (
                    <MotifButton className="more-users">
                      <MotifIcon iconFunction={IconoirPlus} />
                      {Number(sharedUsers.length) - 2}
                    </MotifButton>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <MotifFormField>
        {sharedUsers?.length == 0 && (
          <MotifLabel className="email-label">
            Enter the recipient name or email
          </MotifLabel>
        )}
        <div className={!showUsersSearch ? "hide" : ""}>
          <SearchInput
            aria-label="Search for a user"
            id={`user-search-email-${useId()}`}
            open={!userListLoading}
            className={`email-search-input ${
              searchCharCount >= 3 ? "show-suggestions" : ""
            }`}
            items={
              userList &&
              userList
                .filter((userData: any) => {
                  if (
                    sharedUsers.find(
                      (proxyOwner) => proxyOwner.Email === userData.email,
                    )
                  ) {
                    return false;
                  }
                  if (
                    sharedUserList.find(
                      (sharedUser) => sharedUser.Email === userData.email,
                    )
                  ) {
                    return false;
                  }
                  if (userData.email === emailId) {
                    return false;
                  }
                  const emailRegex =
                    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
                  return emailRegex.test(userData.email);
                })
                .map((userData: any) => {
                  return userData.displayName + " (" + userData.email + ")";
                })
            }
            onChange={(e) => handleSearchInputChange(e)}
            onSelect={(item: any) => handleUserClick(item)}
            visibleOptions={3}
            value={userCurrentSearch}
            multiple={true}
            clearSearch={clearSearch}
          />
          {userCurrentSearch.trim().length > 3 &&
          !userListLoading &&
          userList &&
          !displayItems.length &&
          !checkIfSearchingOwnEmail() ? (
            <MotifErrorMessage
              aria-live="assertive"
              role="alert"
              id="aria-error-primaryList"
            >
              {isAlreadyShared
                ? "Estimate already shared with this user"
                : "Please enter a valid contact information"}
            </MotifErrorMessage>
          ) : (
            <span className="email-supporting-text">
              Users with whom the estimates are shared can view only and it will
              appear in their My Estimates tab.
            </span>
          )}
        </div>
      </MotifFormField>
    </div>
  );
};

export default EmailSearch;
